import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FacilityMeters, useStations, ORDER_STATUS } from "@/components/supply/shared";
import { OrdersPanel } from "@/components/supply/OrdersPanel";
import { StationsPanel } from "@/components/supply/StationsPanel";
import { LedgerPanel, ReconciliationsPanel } from "@/components/supply/LedgerPanel";

const Pipeline = ({ counts }) => (
  <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="order-pipeline">
    {["REQUESTED", "RELEASED", "PICKED_UP", "DELIVERED", "CLOSED", "CANCELLED"].map((s) => (
      <div key={s} className="bg-white border border-slate-200 rounded-sm px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{ORDER_STATUS[s].label}</div>
        <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums mt-1" data-testid={`pipeline-${s}`}>{counts?.[s] ?? 0}</div>
      </div>
    ))}
  </div>
);

export default function Supply() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [stations, reloadStations] = useStations();
  const loadOverview = useCallback(() => api.get("/supply/overview").then(({ data }) => setOverview(data)).catch(() => {}), []);
  useEffect(() => { loadOverview(); }, [loadOverview]);
  const refreshAll = () => { loadOverview(); reloadStations(); };

  return (
    <div className="space-y-6" data-testid="supply-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Stabex partnership</div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Supply chain</h1>
        <p className="text-sm text-slate-500 mt-1">Stations release full cylinders, Gasify riders deliver and bring empties back. Credit and guarantee cover update with every movement.</p>
      </div>
      <FacilityMeters facility={overview?.facility} />
      <Pipeline counts={overview?.order_counts} />
      <Tabs defaultValue="orders">
        <TabsList className="rounded-sm">
          <TabsTrigger value="orders" data-testid="supply-tab-orders">Refill orders</TabsTrigger>
          <TabsTrigger value="stations" data-testid="supply-tab-stations">Stations &amp; stock</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="supply-tab-ledger">Credit &amp; guarantee</TabsTrigger>
          <TabsTrigger value="recon" data-testid="supply-tab-recon">Daily reconciliation</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-4"><OrdersPanel stations={stations} onChanged={refreshAll} /></TabsContent>
        <TabsContent value="stations" className="mt-4"><StationsPanel stations={stations} reload={refreshAll} /></TabsContent>
        <TabsContent value="ledger" className="mt-4"><LedgerPanel facility={overview?.facility} onChanged={refreshAll} isAdmin={user?.role === "admin"} /></TabsContent>
        <TabsContent value="recon" className="mt-4"><ReconciliationsPanel stations={stations} /></TabsContent>
      </Tabs>
    </div>
  );
}
